import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLatest } from '../../hooks/useLatest';
import { useTranslation } from 'react-i18next';
import { FiSearch, FiEye, FiClock, FiCheck, FiX } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { feesAPI, settingsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import Modal from '../../components/Common/Modal';
import AuditTimeline from '../../components/Common/AuditTimeline';
import { UnifiedFilterHeaderCell } from '../../components/Common/UnifiedTableFilters';
import {
  runTableFilters,
  hasActiveClientTableFilters,
  CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT
} from '../../utils/tableFilters';
import './PaymentList.css';

function formatFeeTypeLabel(ft) {
  return (ft || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '—';
}

function formatPaymentMethod(pm) {
  if (!pm) return '—';
  return String(pm).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function verificationBadgeStyle(status) {
  const s = String(status || 'under_verification');
  if (s === 'verified') {
    return { background: 'var(--success-light)', color: 'var(--success)' };
  }
  if (s === 'not_verified') {
    return { background: 'var(--danger-light)', color: 'var(--danger)' };
  }
  return { background: 'var(--warning-light)', color: 'var(--warning)' };
}

const PaymentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminUser = ['super_admin', 'admin'].includes(String(user?.role || '').toLowerCase());
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyPayment, setHistoryPayment] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    fee_type: '',
    currency: ''
  });
  const [tableFilters, setTableFilters] = useState({ globalSearch: '', columns: {} });

  const formatVerificationLabel = (status) => {
    const s = String(status || 'under_verification');
    if (s === 'under_verification') return t('payments.underVerification');
    if (s === 'not_verified') return t('payments.notVerified');
    if (s === 'verified') return t('payments.verified');
    return s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const openHistory = async (paymentRow) => {
    if (!paymentRow?.fee_id) return;
    setHistoryPayment(paymentRow);
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await settingsAPI.getAuditLog({
        entity_type: 'fee',
        entity_id: paymentRow.fee_id,
        limit: 200,
        page: 1
      });
      setHistoryLogs(res.data || []);
    } catch (err) {
      setHistoryLogs([]);
      toast.error(t('payments.toasts.historyFailed'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleFeeVerification = async (feeId, decision) => {
    try {
      await feesAPI.verify(feeId, decision);
      toast.success(decision === 'verified' ? t('payments.toasts.feeVerified') : t('payments.toasts.feeNotVerified'));
      loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.message || t('payments.toasts.verifyFailed'));
    }
  };
  const columnFilterDefs = useMemo(() => {
    const feeTypeOptions = Array.from(new Set((payments || []).map((p) => String(p.fee_type || '')).filter(Boolean)));
    const methodOptions = Array.from(new Set((payments || []).map((p) => String(p.payment_method || '')).filter(Boolean)));
    const verificationOptions = ['under_verification', 'verified', 'not_verified'];
    const currencyOptions = Array.from(new Set((payments || []).map((p) => String(p.currency || '')).filter(Boolean)));
    return [
      { key: 'payment_date', label: t('payments.tablePaymentDate'), field: 'payment_date', type: 'date' },
      { key: 'student_ref', label: t('payments.tableStudentRef'), field: 'student_ref', type: 'text' },
      { key: 'student_name', label: t('payments.tableStudentName'), field: 'student_name', type: 'text' },
      { key: 'fee_type', label: t('payments.tableFeeType'), field: 'fee_type', type: 'multi', options: feeTypeOptions.map((v) => ({ value: v, label: formatFeeTypeLabel(v) })) },
      { key: 'amount_paid', label: t('payments.tableAmount'), field: 'amount_paid', type: 'text' },
      { key: 'reference_no', label: t('payments.tableReference'), field: 'reference_no', type: 'text' },
      { key: 'payment_method', label: t('payments.tablePaymentMethod'), field: 'payment_method', type: 'multi', options: methodOptions.map((v) => ({ value: v, label: formatPaymentMethod(v) })) },
      { key: 'verification_status', label: t('payments.tableVerification'), field: 'verification_status', type: 'multi', options: verificationOptions.map((v) => ({ value: v, label: formatVerificationLabel(v) })) },
      { key: 'currency', label: t('payments.currencies.euro').includes('€') ? 'Currency' : 'Currency', field: 'currency', type: 'multi', options: currencyOptions.map((v) => ({ value: v, label: v })) }
    ];
  }, [payments, t]);
  const columnFilterDefsRef = useLatest(columnFilterDefs);
  const columnFilterMap = useMemo(() => Object.fromEntries(columnFilterDefs.map((d) => [d.key, d])), [columnFilterDefs]);
  const updateColumnFilters = useCallback((updater) => setTableFilters((prev) => ({ ...prev, columns: updater(prev.columns || {}) })), []);
  const normalizedPayments = useMemo(
    () => (payments || []).map((p) => ({ ...p, student_name: `${p.first_name || ''} ${p.last_name || ''}`.trim() })),
    [payments]
  );
  const filteredPayments = useMemo(
    () => runTableFilters(normalizedPayments, tableFilters, tableFilters, [], columnFilterDefs),
    [normalizedPayments, tableFilters, columnFilterDefs]
  );
  const clientTableFilterMode = useMemo(
    () => hasActiveClientTableFilters(tableFilters, columnFilterDefs),
    [tableFilters, columnFilterDefs]
  );

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      if (clientTableFilterMode) {
        params.limit = CLIENT_SIDE_TABLE_FILTER_FETCH_LIMIT;
      }
      const res = await feesAPI.getAllPayments(params);
      setPayments(res.data.payments || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.message || t('payments.toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filters, clientTableFilterMode]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  return (
    <div className="page-container payment-list-page">
      <div className="page-header">
        <div>
          <h1>{t('payments.title')}</h1>
          <p className="page-subtitle">
            {loading ? t('payments.loadingSubtitle') : t('payments.paymentsRecorded', { count: clientTableFilterMode ? filteredPayments.length : total })}
          </p>
        </div>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <div className="toolbar-left payment-list-toolbar">
            <div className="table-search payment-list-search">
              <FiSearch className="search-icon" />
              <input
                type="search"
                placeholder={t('payments.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                autoComplete="off"
              />
            </div>
            <div className="filter-bar payment-list-filters">
              <select value={filters.fee_type} onChange={(e) => setFilters((f) => ({ ...f, fee_type: e.target.value }))}>
                <option value="">{t('payments.allFeeTypes')}</option>
                <option value="application_fee">{t('payments.applicationFee')}</option>
                <option value="tuition_fee">{t('payments.tuitionFee')}</option>
                <option value="registration_fee">{t('payments.registrationFee')}</option>
                <option value="visa_fee">{t('payments.visaFee')}</option>
                <option value="other">{t('payments.otherFee')}</option>
              </select>
              <select value={filters.currency} onChange={(e) => setFilters((f) => ({ ...f, currency: e.target.value }))}>
                <option value="">{t('payments.allCurrencies')}</option>
                <option value="EURO">{t('payments.currencies.euro')}</option>
                <option value="AED">{t('payments.currencies.aed')}</option>
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <div className="table-responsive">
            <table className="payment-list-table">
              <thead>
                <tr>
                  <UnifiedFilterHeaderCell column={columnFilterMap.payment_date} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.student_ref} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.student_name} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.fee_type} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.amount_paid} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.reference_no} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.payment_method} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <UnifiedFilterHeaderCell column={columnFilterMap.verification_status} draftColumns={tableFilters.columns} onChange={updateColumnFilters} />
                  <th>{t('payments.tableReceipt')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((p) => (
                  <tr key={p.fee_id}>
                    <td>
                      {p.payment_date
                        ? new Date(p.payment_date).toLocaleDateString('en-IN', {
                            timeZone: 'Asia/Kolkata',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="payment-list-student-link"
                        onClick={() => navigate(`/students/${p.student_id}`)}
                      >
                        {p.student_ref || '—'}
                      </button>
                    </td>
                    <td>{`${p.first_name || ''} ${p.last_name || ''}`.trim() || '—'}</td>
                    <td>{formatFeeTypeLabel(p.fee_type)}</td>
                    <td className="payment-list-amount">
                      {p.currency || '—'}{' '}
                      {Number(p.amount_paid || 0).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)', maxWidth: 140 }} title={p.reference_no || ''}>
                      {p.reference_no || '—'}
                    </td>
                    <td className="payment-list-method">{formatPaymentMethod(p.payment_method)}</td>
                    <td className="payment-list-verify-cell">
                      <span className="payment-list-status-badge" style={verificationBadgeStyle(p.verification_status)}>
                        {formatVerificationLabel(p.verification_status)}
                      </span>
                      {isAdminUser && p.verification_status === 'under_verification' && (
                        <div className="payment-list-verify-actions">
                          <button
                            type="button"
                            className="btn-verify"
                            title={t('payments.verifyTooltip')}
                            onClick={() => handleFeeVerification(p.fee_id, 'verified')}
                          >
                            <FiCheck size={14} strokeWidth={2.5} /> {t('payments.verifyBtn')}
                          </button>
                          <button
                            type="button"
                            className="btn-reject"
                            title={t('payments.notVerifyTooltip')}
                            onClick={() => handleFeeVerification(p.fee_id, 'not_verified')}
                          >
                            <FiX size={14} strokeWidth={2.5} /> {t('payments.notVerifyBtn')}
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="payment-list-receipt-actions">
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => openHistory(p)}>
                          <FiClock size={14} /> {t('common.history')}
                        </button>
                        {p.payment_receipt_copy ? (
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={async () => {
                              try {
                                const res = await feesAPI.getReceiptUrl(p.fee_id);
                                window.open(res.data.url, '_blank');
                              } catch (err) {
                                toast.error(t('payments.toasts.receiptFailed'));
                              }
                            }}
                          >
                            <FiEye size={14} /> {t('payments.viewReceipt')}
                          </button>
                        ) : (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '6px 0' }}>
                            {t('payments.noFile')}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredPayments.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center" style={{ padding: 40, color: 'var(--text-muted)' }}>
                      {t('payments.noPayments')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryPayment(null);
          setHistoryLogs([]);
          setHistoryLoading(false);
        }}
        title={
          historyPayment
            ? `${t('payments.historyTitle')} — ${historyPayment.student_ref || `#${historyPayment.student_id || '—'}`}`
            : t('payments.historyTitle')
        }
        size="lg"
        footer={<button type="button" className="btn btn-outline" onClick={() => setHistoryOpen(false)}>{t('common.close')}</button>}
      >
        {historyLoading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : (
          <AuditTimeline logs={historyLogs} showEntity={false} />
        )}
      </Modal>
    </div>
  );
};

export default PaymentList;
